// src/stores/settingsStore.js
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import * as settingsService from '../services/settingsService';

const defaultSettings = {
  // Theme settings
  theme: 'light',
  direction: 'ltr',
  language: 'en',
  hideAnimations: false,
  colorPalette: 'original', // Default color palette
  
  // Display settings
  dateFormat: 'DD/MM/YYYY',
  timeFormat: '24',
  timezone: 'UTC',
  
  // Notification settings
  notifications: {
    email: {
      contentApproval: true,
      campaigns: true,
      reports: true,
      billing: true,
      systemUpdates: false,
    },
    sms: {
      contentApproval: false,
      campaigns: false,
      reports: false,
      billing: false,
      systemUpdates: false,
    },
    inApp: {
      contentApproval: true,
      campaigns: true,
      reports: true,
      billing: true,
      systemUpdates: true,
    },
    browser: true,
    sound: true,
  },
  
  // User preferences
  pageSize: 25,
  
  // Dashboard preferences
  preferences: {
    dashboard: {
      collapsedSections: {
        pending: false,
        inProgress: false,
        completed: true // Default to collapsed
      },
      hideCompleted: false,
      completedDaysLimit: 30
    }
  }
};

const useSettingsStore = create(
  persist(
    (set, get) => ({
      // Settings state
      settings: defaultSettings,
      systemSettings: {},
      clientSettings: {},
      hrSettings: null,
      isLoading: false,
      error: null,
      
      // Initialize settings
      initSettings: async () => {
        const settings = get().settings;
        
        // Apply settings to document
        document.documentElement.setAttribute('dir', settings.direction);
        document.documentElement.setAttribute('lang', settings.language);
        
        try {
          // Fetch user settings from API if authenticated
          const userSettings = await settingsService.getUserSettings();
          set({
            settings: {
              ...settings,
              ...userSettings.settings,
            }
          });
          return userSettings;
        } catch (error) {
          console.log('Failed to fetch user settings:', error);
          return null;
        }
      },
      
      // Update a single setting
      updateSetting: async (key, value) => {
        set(state => ({
          settings: {
            ...state.settings,
            [key]: value,
          }
        }));
        
        // Handle special cases that need DOM updates
        if (key === 'direction') {
          document.documentElement.setAttribute('dir', value);
        }
        
        if (key === 'language') {
          document.documentElement.setAttribute('lang', value);
        }
        
        try {
          // Save setting to API
          await settingsService.updateUserSetting(key, value);
          return true;
        } catch (error) {
          console.log('Failed to save setting:', error);
          return false;
        }
      },
      
      // Update nested setting (e.g., notifications.email.contentApproval)
      updateNestedSetting: async (parent, key, value) => {
        if (key) {
          // Update specific nested setting
          set(state => ({
            settings: {
              ...state.settings,
              [parent]: {
                ...state.settings[parent],
                [key]: value,
              }
            }
          }));
        } else {
          // Replace entire parent object
          set(state => ({
            settings: {
              ...state.settings,
              [parent]: value,
            }
          }));
        }
        
        try {
          // Save nested setting to API
          await settingsService.updateNestedUserSetting(parent, key, value);
          return true;
        } catch (error) {
          console.log('Failed to save nested setting:', error);
          return false;
        }
      },
      
      // Update entire settings object (useful for preferences)
      updateSettings: async (newSettings) => {
        set(state => ({
          settings: {
            ...state.settings,
            ...newSettings
          }
        }));
        
        try {
          // Save settings to API
          await settingsService.updateUserSettings(newSettings);
          return true;
        } catch (error) {
          console.log('Failed to save settings:', error);
          return false;
        }
      },
      
      // Reset settings to defaults
      resetSettings: async () => {
        set({ settings: defaultSettings });
        
        try {
          // Reset settings in API
          await settingsService.resetUserSettings();
          
          // Update document attributes
          document.documentElement.setAttribute('dir', defaultSettings.direction);
          document.documentElement.setAttribute('lang', defaultSettings.language);
          
          return true;
        } catch (error) {
          console.log('Failed to reset settings:', error);
          return false;
        }
      },
      
      // System settings for admin users
      fetchSystemSettings: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await settingsService.getSystemSettings();
          set({ 
            systemSettings: response.settings || {},
            isLoading: false 
          });
          return response.settings;
        } catch (error) {
          set({ 
            isLoading: false,
            error: error.message || 'Failed to fetch system settings'
          });
         console.log('error', error);
        }
      },
      
      updateSystemSetting: async (key, value) => {
        set({ isLoading: true, error: null });
        try {
          await settingsService.updateSystemSetting(key, value);
          set(state => ({
            systemSettings: {
              ...state.systemSettings,
              [key]: value
            },
            isLoading: false
          }));
          return true;
        } catch (error) {
          set({ 
            isLoading: false,
            error: error.message || 'Failed to update system setting'
          });
         console.log('error', error);
        }
      },
      
      createSystemSetting: async (settingData) => {
        set({ isLoading: true, error: null });
        try {
          const response = await settingsService.createSystemSetting(settingData);
          set(state => ({
            systemSettings: {
              ...state.systemSettings,
              [settingData.key]: settingData.value
            },
            isLoading: false
          }));
          return response;
        } catch (error) {
          set({ 
            isLoading: false,
            error: error.message || 'Failed to create system setting'
          });
         console.log('error', error);
        }
      },
      
      // Client settings functions
      fetchClientSettings: async (clientId) => {
        set({ isLoading: true, error: null });
        try {
          const response = await settingsService.getClientSettings(clientId);
          set(state => ({
            clientSettings: {
              ...state.clientSettings,
              [clientId]: response.settings || {}
            },
            isLoading: false
          }));
          return response.settings;
        } catch (error) {
          set({ 
            isLoading: false,
            error: error.message || 'Failed to fetch client settings'
          });
         console.log('error', error);
        }
      },
      
      updateClientSetting: async (clientId, key, value) => {
        set({ isLoading: true, error: null });
        try {
          await settingsService.updateClientSetting(clientId, key, value);
          set(state => ({
            clientSettings: {
              ...state.clientSettings,
              [clientId]: {
                ...(state.clientSettings[clientId] || {}),
                [key]: value
              }
            },
            isLoading: false
          }));
          return true;
        } catch (error) {
          set({ 
            isLoading: false,
            error: error.message || 'Failed to update client setting'
          });
         console.log('error', error);
        }
      },
      
      // Email settings
      fetchEmailSettings: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await settingsService.getEmailSettings();
          set(state => ({
            systemSettings: {
              ...state.systemSettings,
              email: response.settings || {}
            },
            isLoading: false
          }));
          return response.settings;
        } catch (error) {
          set({ 
            isLoading: false,
            error: error.message || 'Failed to fetch email settings'
          });
         console.log('error', error);
        }
      },
      
      updateEmailSettings: async (settings) => {
        set({ isLoading: true, error: null });
        try {
          await settingsService.updateEmailSettings(settings);
          set(state => ({
            systemSettings: {
              ...state.systemSettings,
              email: settings
            },
            isLoading: false
          }));
          return true;
        } catch (error) {
          set({ 
            isLoading: false,
            error: error.message || 'Failed to update email settings'
          });
         console.log('error', error);
        }
      },
      
      testEmailSettings: async (email) => {
        set({ isLoading: true, error: null });
        try {
          const response = await settingsService.testEmailSettings(email);
          set({ isLoading: false });
          return response;
        } catch (error) {
          set({ 
            isLoading: false,
            error: error.message || 'Failed to test email settings'
          });
         console.log('error', error);
        }
      },
      
      // SMS settings
      fetchSmsSettings: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await settingsService.getSmsSettings();
          set(state => ({
            systemSettings: {
              ...state.systemSettings,
              sms: response.settings || {}
            },
            isLoading: false
          }));
          return response.settings;
        } catch (error) {
          set({ 
            isLoading: false,
            error: error.message || 'Failed to fetch SMS settings'
          });
         console.log('error', error);
        }
      },
      
      updateSmsSettings: async (settings) => {
        set({ isLoading: true, error: null });
        try {
          await settingsService.updateSmsSettings(settings);
          set(state => ({
            systemSettings: {
              ...state.systemSettings,
              sms: settings
            },
            isLoading: false
          }));
          return true;
        } catch (error) {
          set({ 
            isLoading: false,
            error: error.message || 'Failed to update SMS settings'
          });
         console.log('error', error);
        }
      },
      
      testSmsSettings: async (phoneNumber) => {
        set({ isLoading: true, error: null });
        try {
          const response = await settingsService.testSmsSettings(phoneNumber);
          set({ isLoading: false });
          return response;
        } catch (error) {
          set({ 
            isLoading: false,
            error: error.message || 'Failed to test SMS settings'
          });
         console.log('error', error);
        }
      },
      
      // Import/Export settings
      exportSettings: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await settingsService.exportSettings();
          set({ isLoading: false });
          return response;
        } catch (error) {
          set({ 
            isLoading: false,
            error: error.message || 'Failed to export settings'
          });
         console.log('error', error);
        }
      },
      
      importSettings: async (settingsData) => {
        set({ isLoading: true, error: null });
        try {
          const response = await settingsService.importSettings(settingsData);
          
          // Update local settings
          if (response.settings) {
            set({ settings: response.settings });
            
            // Update document attributes
            if (response.settings.direction) {
              document.documentElement.setAttribute('dir', response.settings.direction);
            }
            
            if (response.settings.language) {
              document.documentElement.setAttribute('lang', response.settings.language);
            }
          }
          
          set({ isLoading: false });
          return response;
        } catch (error) {
          set({ 
            isLoading: false,
            error: error.message || 'Failed to import settings'
          });
         console.log('error', error);
        }
      },
      
      // HR Settings
      fetchHRSettings: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await settingsService.getHRSettings();
          console.log('🔧 Store: Raw HR Settings response:', response);
          console.log('🔧 Store: Response type:', typeof response);
          console.log('🔧 Store: Response keys:', response ? Object.keys(response) : 'null');
          
          // Check if response is wrapped or direct
          let hrSettings;
          if (response && response.status === 'success' && response.data) {
            // Wrapped response: { status: 'success', data: hrSettings }
            hrSettings = response.data;
          } else if (response && (response.departments !== undefined || response.positions !== undefined)) {
            // Direct response: hrSettings object
            hrSettings = response;
          } else {
            // Default fallback
            hrSettings = {
              departments: [],
              positions: [],
              employeeLevels: [],
              leaveTypes: []
            };
          }
          
          console.log('🔧 Store: Processed HR Settings:', hrSettings);
          console.log('🔧 Store: Departments in hrSettings:', hrSettings.departments);
          console.log('🔧 Store: Positions in hrSettings:', hrSettings.positions);
          
          set({ 
            hrSettings,
            isLoading: false 
          });
          return hrSettings;
        } catch (error) {
          set({ 
            isLoading: false,
            error: error.message || 'Failed to fetch HR settings'
          });
          console.log('error', error);
        }
      },
      
      updateHRSetting: async (settings) => {
        set({ isLoading: true, error: null });
        try {
          const response = await settingsService.updateHRSettings(settings);
          // The backend returns { status: 'success', data: updatedSettings }
          const updatedSettings = response.data || settings;
          set({
            hrSettings: updatedSettings,
            isLoading: false
          });
          return true;
        } catch (error) {
          set({ 
            isLoading: false,
            error: error.message || 'Failed to update HR settings'
          });
          console.log('error', error);
        }
      },
      
      // Clear error state
      clearError: () => set({ error: null })
    }),
    {
      name: 'aghrba-settings-storage',
      partialize: (state) => ({ settings: state.settings }),
    }
  )
);

export default useSettingsStore;